import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useField } from 'react-final-form';
import equal from 'fast-deep-equal';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { getIsPlanErknmStatusConsidered } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmDataErknmApproveApproved,
  getKnmDataNote,
  getKnmFormRef,
  getKnmIsChangePlanReasonActive,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsNew,
  getTargetEntitySelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { createTypedUseFormValue } from 'hooks/useTypedFormField';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { getDistrictSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getDecisionDataCreatedAt } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { FnsCheckStatusesEnum } from '../enums/FnsCheckStatusesEnum';
import styles from './ErknmSubjectInfo.module.scss';
import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { MAX_CHARACTERS_ERROR, REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { makeMaxCharactersValidator } from '../../../CorePackage/MainModule/validators/maxCharacters';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from '../../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { KnmSnilsField } from '../../../KnmPackage/MainModule/components/KnmSnilsField';
import { ICountry } from '../../../APIPackage/CatalogsModule/interfaces/ICountry';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultFieldEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import {
  isFnsAddressFnsError,
  isOrganizationNameFnsError,
  isWorkAddressFnsError,
  renderFnsCheckMessage,
  renderFnsErvkEqualsError,
  valuesSubjectResultStatuses,
} from '../../../CorePackage/MainModule/common/renderFnsMessage';
import { fnsCheckMessageByStatuses } from '../constants/fnsCheckMessageByStatuses';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { KnmFiasAddressField } from '../../../KnmPackage/MainModule/components/KnmFiasAddressField';
import { useErknmSubjectHandlers } from '../hooks/useErknmSubjectHandlers';
import { useErknmSubjectFields } from '../hooks/useErknmSubjectFields';
import { useSyncManualAddress } from '../hooks/useSyncManualAddress';
import { rafpNotFoundMessage } from '../constants/erknmSubjectConstants';

export enum KnmSubjectTypesEnum {
  FIZ = 'Физические лица',
  UR = 'Юридические лица',
  IP = 'Индивидуальные предприниматели',
  IFIZ = 'Иностранные физические лица',
  IUR = 'Иностранные юридические лица',
  FIUR = 'Филиалы иностранных юридических лиц',
  OFFICIAL = 'Должностные лица',
}

interface IErknmSubjectInfoProps {
  ervkId: string | undefined;
  firstOrganizationName: string | undefined;
  checkSubjectResult: IKnmCheckSubjectResult | null;
  inn: string | undefined;
  innLabelBlockId: string;
  inspectionResults?: IInspectionResult[];
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  isExtended: boolean;
  isNewOrganizationTemplate?: boolean;
  isWatchOnly: boolean;
  kpp: string | undefined;
  kppLabelBlockId: string;
  lookupData?: ILegalEntityLookup | boolean | undefined;
  name: string | undefined;
  ogrn: string | undefined;
  ogrnLabelBlockId: string;
  organizationNameBlockId: string;
  rafpLabelBlockId: string;
  rafpNza: string | undefined;
  setCheckByInnMessage?: Dispatch<SetStateAction<string | undefined>>;
  setLookupData?: Dispatch<SetStateAction<ILegalEntityLookup | boolean | undefined>>;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectResult: IKnmCheckSubjectResult | null;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  disabledByChose?: boolean;
  isChangeSubjectTypeFromActionsAvailable?: boolean;
}

interface IErknmSubjectInfoStoreProps {
  formRef: IKnmFormRef | undefined;
  isChangePlanReasonActive: boolean;
  isEditableInStatusCommentsExist: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isNewKnm: boolean;
  isSupervisor: boolean | undefined;
  knmDataApprovedType: ApprovedTypesEnum | null | undefined;
  knmDataNote: string | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
  districtSelectOptions: IKnmDistrictSelectOption[];
  settings: IAdminSetting[];
  created: string | null | undefined;
  decisionDataCreated?: string | null;
}

const useFieldValue = createTypedUseFormValue<IKnmErknmForm>();

// // @ts-ignore
// const Condition = ({ when, is, children }) => (
//   <Field name={when} subscription={{ value: true }}>
//     {({ input: { value } }) => (value === is ? children : null)}
//   </Field>
// );

const ErknmSubjectInfoWithoutStore = ({
  ervkId,
  firstOrganizationName,
  checkSubjectResult,
  inn,
  innLabelBlockId,
  inspectionResults,
  isAutocomplete,
  isExtended,
  isNewOrganizationTemplate,
  isWatchOnly,
  kpp,
  kppLabelBlockId,
  lookupData,
  name,
  ogrn,
  ogrnLabelBlockId,
  organizationNameBlockId,
  rafpLabelBlockId,
  rafpNza,
  setCheckByInnMessage,
  setLookupData,
  smevRequestStatus,
  snils,
  subjectResult,
  subjectType,
  formRef,
  isChangePlanReasonActive,
  isEditableInStatusCommentsExist,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isNewKnm,
  isSupervisor,
  knmDataApprovedType,
  knmDataNote,
  knmPlanStatus,
  subjectTypeOptions,
  disabledByChose,
  districtSelectOptions,
  settings,
  isChangeSubjectTypeFromActionsAvailable,
  created,
  decisionDataCreated,
}: IErknmSubjectInfoProps & IErknmSubjectInfoStoreProps) => {
  const isKnmPlanStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanStatus);

  const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
  const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;
  const isUR = subjectType?.label === KnmSubjectTypesEnum.UR;
  const isIP = subjectType?.label === KnmSubjectTypesEnum.IP;
  const isFIUR = subjectType?.label === KnmSubjectTypesEnum.FIUR;

  const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();
  const fnsCheckMessage = fnsCheckMessageByStatuses.find(item =>
    equal(item.statuses.sort(), valuesSubjectResultStatuses(subjectResult)?.sort()),
  );
  const nameWithoutDot = name?.endsWith('.') ? name?.slice(0, -1) : name;

  const subjectFieldNames = useErknmSubjectFields({ name: nameWithoutDot });

  const isImpossibilityOfPrevious =
    useFieldValue('reasonsErknm')?.[0]?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;

  const isLaterThanHideFields4129 = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.SETTING_HIDE_FIELDS_4_1_29,
  );

  const [rafpNzaNotFoundText, setRafpNzaNotFoundText] = useState<string | undefined>(rafpNotFoundMessage);
  const [isFnsCheckInProgress, setIsFnsCheckInProgress] = useState(
    fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.IN_PROGRESS,
  );
  const [isFnsCheckFound, setIsFnsCheckFound] = useState(
    fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.FOUND,
  );

  useEffect(() => {
    if (inn && ogrn) {
      dispatchKnmSetLegalEntityData({ inn, ogrn });
    }
  }, [dispatchKnmSetLegalEntityData, inn, ogrn]);

  useEffect(() => {
    if (!isAutocomplete && isFIZ) {
      formRef?.current.change(subjectFieldNames.ogrn as keyof IKnmForm, undefined);
      if (isExtended) {
        formRef?.current.change(`${name}.okveds` as keyof IKnmForm, undefined);
      }
    }
  }, [formRef, isAutocomplete, isFIZ, isExtended, name, subjectFieldNames.ogrn]);

  useEffect(() => {
    setIsFnsCheckInProgress(fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.IN_PROGRESS);
    setIsFnsCheckFound(fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.FOUND);
  }, [fnsCheckMessage]);

  const isFieldEnabledFirstCase =
    ((!isWatchOnly &&
      !isFnsCheckInProgress &&
      isSupervisor &&
      (isNewOrganizationTemplate || isNewKnm || isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote)) ||
      ((isChangePlanReasonActive || isEditableInStatusCommentsExist) && !ervkId)) &&
    !disabledByChose;

  const isFieldEnabledSecondCase = (isChangePlanReasonActive || isEditableInStatusCommentsExist) && !ervkId;
  const isFieldEnabledThirdCase = isChangeSubjectTypeFromActionsAvailable && !ervkId;
  const isFieldEnabled =
    (isFieldEnabledFirstCase || isFieldEnabledSecondCase || isFieldEnabledThirdCase) &&
    !disabledByChose &&
    !isImpossibilityOfPrevious;

  useSyncManualAddress({
    formRef,
    name,
    prefix: 'location',
    targetFieldName: subjectFieldNames.locationAddress,
    shouldSync: isUR || isFIUR,
  });

  useSyncManualAddress({
    formRef,
    name,
    prefix: 'work',
    targetFieldName: subjectFieldNames.workAddress,
    shouldSync: isUR,
  });

  useSyncManualAddress({
    formRef,
    name,
    prefix: 'factual',
    targetFieldName: subjectFieldNames.factualAddress,
    shouldSync: isIP,
  });

  const isAutocompleteValue = useField(subjectFieldNames.isAutocomplete, { subscription: { value: true } }).input.value;

  const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
  const isRafpNzaNotFound = smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
  const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
  const isInnFieldEnabled = isFieldEnabled || isRafpNzaNotFound;

  const isOrganizationNameFieldEnabledWithoutFiz =
    !isWatchOnly &&
    isSupervisor &&
    (isNewOrganizationTemplate || isNewKnm || isKnmStatusFillInProcess || (isKnmStatusReadyForApprove && isFIZ)) &&
    !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote);

  const isOrganizationNameFieldEnabled = isFIZ && isOrganizationNameFieldEnabledWithoutFiz;
  const getOrganizationNameFieldRequired = () => !(isOrganizationNameFieldEnabledWithoutFiz && isFIZ);

  const {
    handleLegalEntityAutoCompleteSelect,
    handleAutoCompleteClear,
    clearItAccreditation,
    handleSubjectTypeChange,
    handleInnOrKppChange,
    handleRafpNzaChange,
  } = useErknmSubjectHandlers({
    formRef,
    name,
    subjectType,
    isUR,
    isIP,
    isExtended,
    districtSelectOptions,
    setCheckByInnMessage,
    isKnmStatusFillInProcess,
    isChangeSubjectTypeFromActionsAvailable,
    setRafpNzaNotFoundText,
    subjectFieldNames,
  });

  const normalizeFio = (value: string | undefined) => {
    if (!value) return value;

    return value.replace(/[^а-яёa-z-`' ]/gi, '');
  };

  const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);
  const saveKnmAndWaitFromRafpWarning =
    (isRafpFilled || (inn && kpp)) && !smevRequestStatus
      ? 'Сохраните КНМ и ожидайте загрузки информации из РАФП'
      : undefined;
  const waitFromRafpWarning = isRafpNzaSent ? 'Ожидайте загрузки информации из РАФП' : undefined;
  const rafpWarning = isFIUR ? saveKnmAndWaitFromRafpWarning || waitFromRafpWarning : undefined;
  const innOrSnilsWarning = isFIZ && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
  const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
  const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

  const IROrganizationName = inspectionResults?.find(
    i =>
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NAME ||
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_NAME,
  );
  const IRLocationAddress = inspectionResults?.find(
    i =>
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_ADDRESS ||
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_ADDRESS,
  );
  const IRFactualAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_FACTUAL_ADDRESS,
  );
  const IRRafpNza = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.RAFP_REGISTRY);

  return (
    <>
      <Row>
        <FieldBlock
          label={
            <>
              Тип контролируемого лица
              <ErknmFieldMark fieldName={subjectFieldNames.subjectType} />
            </>
          }
          size="half"
          tooltip={
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующем поле:
                  <ul>
                    <p>Контрольный (надзорный) орган</p>
                    <p>Вид контроля (надзора)</p>
                    <p>Основания проведения КНМ</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          }
        >
          <SelectInputField
            id={subjectFieldNames.subjectType}
            name={subjectFieldNames.subjectType}
            options={subjectTypeOptions}
            disabled={!isFieldEnabled}
            size="large"
            onChange={handleSubjectTypeChange}
          />
        </FieldBlock>
      </Row>

      {subjectType && (
        <>
          <Row margin="none">
            {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.IP) && (
              <FieldBlock
                label="Основной государственный регистрационный номер (ОГРН)"
                size="half"
                color="black"
                id={ogrnLabelBlockId}
              />
            )}
            {subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ && (
              <FieldBlock
                label={
                  <>
                    Идентификационный номер налогоплательщика (ИНН)
                    <ErknmFieldMark fieldName={subjectFieldNames.inn} />
                  </>
                }
                size="half"
                color="black"
                id={innLabelBlockId}
              />
            )}
            {subjectType.label === KnmSubjectTypesEnum.FIUR && (
              <FieldBlock
                label={
                  <>
                    Номер записи об аккредитации в РАФП
                    <ErknmFieldMark fieldName={subjectFieldNames.rafpNza} />
                  </>
                }
                size="half"
                id={rafpLabelBlockId}
                color="black"
              />
            )}
            {isFIZ && (
              <FieldBlock
                label={
                  <>
                    Страховой номер индивидуального лицевого счета (СНИЛС)
                    <ErknmFieldMark fieldName={subjectFieldNames.snils} />
                  </>
                }
                size="half"
                color="black"
              />
            )}
          </Row>

          <Row>
            {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.IP) && (
              <FieldBlock size="half">
                <KnmOgrnField
                  name={subjectFieldNames.ogrn}
                  clearable={isFieldEnabled && isAutocomplete}
                  disabled={!isFieldEnabled || isAutocomplete}
                  ogrn={ogrn}
                  onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                  onClear={handleAutoCompleteClear}
                  onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                />
              </FieldBlock>
            )}

            {subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ && (
              <FieldBlock size="half">
                <KnmInnField
                  name={subjectFieldNames.inn}
                  clearable={isInnFieldEnabled && isAutocomplete}
                  disabled={!isInnFieldEnabled || isAutocomplete || !!rafpNza || isRafpNzaSent}
                  inn={inn}
                  isFnsCheckFound={isFnsCheckFound}
                  isFiz={isFIZ}
                  setLookupData={setLookupData}
                  isShowAutoCompleteField={
                    subjectType.label !== KnmSubjectTypesEnum.IUR && subjectType.label !== KnmSubjectTypesEnum.FIUR
                  }
                  onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                  onClear={handleAutoCompleteClear}
                  onLoadingChange={setFnsLookupByInnLoadingFlag}
                  warning={
                    innOrSnilsWarning ||
                    rafpWarning ||
                    (!isFIZ && !smevRequestStatus && renderFnsCheckMessage(subjectResult, lookupData, isAutocomplete))
                  }
                  onChangeInn={clearItAccreditation}
                  setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
                  ervkId={ervkId}
                  subjectType={subjectType}
                  checkSubjectResult={checkSubjectResult}
                  isShowAdditionalMessages={true}
                />
                {isRafpNzaNotFound && inn && kpp && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
              </FieldBlock>
            )}

            {subjectType.label === KnmSubjectTypesEnum.FIUR && (
              <FieldBlock size="half">
                <TextInputField
                  name={subjectFieldNames.rafpNza}
                  disabled={!!inn || !!kpp || (!isRafpNzaNotFound && isRafpNzaSent)}
                  onChange={e => handleRafpNzaChange(e.target.value || undefined)}
                  warning={rafpWarning}
                  inspectionResult={IRRafpNza}
                />
                {isRafpNzaNotFound && !inn && !kpp && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
                {isRafpNzaError && (
                  <Hint
                    text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос будет
                    произведен через час"
                  />
                )}
              </FieldBlock>
            )}
            {isFIZ && (
              <FieldBlock size="half">
                <KnmSnilsField name={subjectFieldNames.snils} warning={innOrSnilsWarning} disabled={!isFieldEnabled} />
              </FieldBlock>
            )}
          </Row>
          {subjectType.label === KnmSubjectTypesEnum.FIUR && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Код причины постановки на учет (КПП)
                    <ErknmFieldMark fieldName={subjectFieldNames.kpp} />
                  </>
                }
                size="half"
                id={kppLabelBlockId}
                color="black"
              >
                <TextInputField
                  name={subjectFieldNames.kpp}
                  disabled={isWatchOnly || !!rafpNza || isRafpNzaSent}
                  onChange={e => handleInnOrKppChange('kpp', e.target.value || undefined)}
                  warning={rafpWarning}
                />
                {isRafpNzaNotFound && (!!inn || !!kpp) && (
                  <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                )}
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR ||
            subjectType.label === KnmSubjectTypesEnum.IP ||
            subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock label={'Наименование проверяемого лица'} id={organizationNameBlockId} color="black">
                <TextareaField
                  id={organizationNameBlockId}
                  inputClassName={styles.OrganizationNameInput}
                  name={subjectFieldNames.organizationName}
                  resize="none"
                  clearable={isOrganizationNameFieldEnabled && isAutocomplete}
                  disabled={!isOrganizationNameFieldEnabled}
                  onClear={handleAutoCompleteClear}
                  inspectionResult={IROrganizationName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                />
                {!smevRequestStatus &&
                  isOrganizationNameFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {(isIFIZ || subjectType.label === KnmSubjectTypesEnum.FIZ) && (
            <Row>
              <FieldBlock
                id={organizationNameBlockId}
                label={
                  <>
                    Фамилия, имя, отчество (при наличии)
                    <ErknmFieldMark fieldName={subjectFieldNames.organizationName} />
                  </>
                }
                color="black"
              >
                <TextInputField
                  name={subjectFieldNames.organizationName}
                  disabled={!isFieldEnabled}
                  id={organizationNameBlockId}
                  parse={normalizeFio}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Адрес места нахождения
                    <ErknmFieldMark fieldName={`${name}.address`} />
                  </>
                }
                color="black"
              >
                <KnmFiasAddressField
                  id={subjectFieldNames.locationAddress}
                  addressFieldPathWithPrefix={subjectFieldNames.locationAddress}
                  required
                  disabled={true}
                  formRef={formRef}
                  prefixNameNotInFias={`${name}.location`}
                  manualAddressPlaceholder={
                    <>
                      Место
                      <ErknmFieldMark fieldName={subjectFieldNames.locationmanualAddress} />
                    </>
                  }
                  regionPlaceholder={
                    <>
                      Субъект РФ
                      <ErknmFieldMark fieldName={subjectFieldNames.locationregion} />
                    </>
                  }
                  inspectionResult={IRLocationAddress}
                  onClear={handleAutoCompleteClear}
                  hideManualAddress={!!ervkId || !!isAutocompleteValue}
                  fieldPaths={{
                    notInFIAS: subjectFieldNames.locationnotInFIAS,
                    isErrorFiasValue: subjectFieldNames.locationisErrorFiasValue,
                  }}
                />
                {!smevRequestStatus &&
                  isFnsAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.UR && !isLaterThanHideFields4129 && (
            <Row>
              <FieldBlock label="Место нахождения (осуществления деятельности) контролируемого лица" color="black">
                <TextInputField
                  name={subjectFieldNames.workAddress}
                  disabled={isWatchOnly || !!isAutocompleteValue || !isFieldEnabled || !!ervkId}
                />
                {!smevRequestStatus &&
                  isWorkAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IP && !isLaterThanHideFields4129 && (
            <Row>
              <FieldBlock label="Место фактического осуществления деятельности" color="black">
                <TextInputField
                  name={subjectFieldNames.factualAddress}
                  inspectionResult={IRFactualAddress}
                  disabled={isWatchOnly || !!isAutocompleteValue || !isFieldEnabled || !!ervkId}
                />
              </FieldBlock>
            </Row>
          )}
          {isIFIZ && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Сведения о документе, удостоверяющем личность
                    <ErknmFieldMark fieldName={subjectFieldNames.document} />
                  </>
                }
                color="black"
              >
                <TextInputField name={subjectFieldNames.document} parse={parsePassport} disabled={!isFieldEnabled} />
              </FieldBlock>
            </Row>
          )}
          {firstOrganizationName && (
            <Row>
              <FieldBlock label="Первоначальное наименование: " button={firstOrganizationName} />
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IUR && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Наименование
                    <ErknmFieldMark fieldName={subjectFieldNames.organizationName} />
                  </>
                }
                id={organizationNameBlockId}
              >
                <TextInputField
                  id={organizationNameBlockId}
                  name={subjectFieldNames.organizationName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                  disabled={!isFieldEnabled}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.IUR || isIFIZ) && (
            <Row>
              <FieldBlock
                label={
                  <>
                    {subjectType.label === KnmSubjectTypesEnum.IUR
                      ? 'Государство, в котором указанное юридическое лицо зарегистрировано'
                      : 'Гражданство (при наличии)'}
                    <ErknmFieldMark fieldName={subjectFieldNames.country} />
                  </>
                }
                color="black"
              >
                <SelectInputFieldAutoOptions
                  id={subjectFieldNames.country}
                  name={subjectFieldNames.country}
                  isClearable={false}
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.COUNTRIES}
                  disabled={!isFieldEnabled}
                  transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                />
              </FieldBlock>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  isChangePlanReasonActive: getKnmIsChangePlanReasonActive(state) || false,
  isEditableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isNewKnm: getKnmIsNew(state) || false,
  isSupervisor: getUserRoleIsSupervisor(state),
  knmDataApprovedType: getKnmDataErknmApproveApproved(state),
  knmDataNote: getKnmDataNote(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
  districtSelectOptions: getDistrictSelectOptions(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  decisionDataCreated: getDecisionDataCreatedAt(state),
});

export const ErknmSubjectInfo = connect(mapStateToProps)(ErknmSubjectInfoWithoutStore);
